/**
 * Billing and Financial React Query Hooks
 * 
 * Provides React Query hooks for billing and financial operations including:
 * - Invoice management (list, view, create)
 * - Payment tracking and processing
 * - Billing statistics and financial KPIs
 * 
 * All hooks use React Query for automatic caching, background refetching,
 * and optimistic updates with proper cache invalidation.
 * 
 * @module hooks/useBilling
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { API_ENDPOINTS } from '../config/api.config.js';

/**
 * Fetches invoices with optional filtering
 * 
 * @param {Object} filters - Query parameters for filtering invoices
 * @param {string} [filters.status] - Filter by invoice status
 * @param {string} [filters.customer_id] - Filter by customer
 * @param {string} [filters.start_date] - Filter by start date
 * @param {string} [filters.end_date] - Filter by end date
 * @returns {Object} React Query result with invoices data
 * @property {Array} data - Array of invoice records
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useInvoices = (filters = {}) => {
  return useQuery({
    queryKey: ['invoices', filters],
    queryFn: async () => {
      const response = await api.get('/api/v1/billing/invoices', { params: filters });
      return response.data;
    },
  });
};

/**
 * Fetches a single invoice by ID
 * 
 * Query is disabled if invoiceId is not provided
 * 
 * @param {string} invoiceId - Invoice ID
 * @returns {Object} React Query result with invoice data
 * @property {Object} data - Invoice record
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useInvoice = (invoiceId) => {
  return useQuery({
    queryKey: ['invoices', invoiceId],
    queryFn: async () => {
      const response = await api.get(`/api/v1/billing/invoices/${invoiceId}`);
      return response.data;
    },
    enabled: !!invoiceId,
  });
};

/**
 * Mutation hook to create a new invoice
 * 
 * Automatically invalidates invoices query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const createInvoice = useCreateInvoice();
 * createInvoice.mutate({ 
 *   customer_id: '123', 
 *   items: [...], 
 *   due_date: '2025-12-31' 
 * });
 */
export const useCreateInvoice = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/billing/invoices', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'stats'] });
    },
  });
};

/**
 * Mutation hook to update an invoice
 * 
 * Automatically invalidates invoices and billing stats queries on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const updateInvoice = useUpdateInvoice();
 * updateInvoice.mutate({ 
 *   invoiceId: '123', 
 *   data: { status: 'paid' } 
 * });
 */
export const useUpdateInvoice = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ invoiceId, data }) => {
      const response = await api.patch(`${API_ENDPOINTS.BILLING}/invoices/${invoiceId}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'stats'] });
    },
  });
};

/**
 * Mutation hook to delete an invoice
 * 
 * Automatically invalidates invoices and billing stats queries on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const deleteInvoice = useDeleteInvoice();
 * deleteInvoice.mutate('invoice_123');
 */
export const useDeleteInvoice = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (invoiceId) => {
      const response = await api.delete(`${API_ENDPOINTS.BILLING}/invoices/${invoiceId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'stats'] });
    },
  });
};

/**
 * Fetches payments with optional filtering
 * 
 * @param {Object} filters - Query parameters for filtering payments
 * @param {string} [filters.status] - Filter by payment status
 * @param {string} [filters.invoice_id] - Filter by invoice
 * @param {string} [filters.start_date] - Filter by start date
 * @param {string} [filters.end_date] - Filter by end date
 * @returns {Object} React Query result with payments data
 * @property {Array} data - Array of payment records
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const usePayments = (filters = {}) => {
  return useQuery({
    queryKey: ['payments', filters],
    queryFn: async () => {
      const response = await api.get('/api/v1/billing/payments', { params: filters });
      return response.data;
    },
  });
};

/**
 * Fetches billing statistics and financial KPIs
 * 
 * @param {Object} params - Query parameters for filtering stats
 * @param {string} [params.start_date] - Start date for statistics period
 * @param {string} [params.end_date] - End date for statistics period
 * @returns {Object} React Query result with billing statistics
 * @property {Object} data - Billing statistics object
 * @property {number} data.total_revenue - Total revenue
 * @property {number} data.outstanding_invoices - Number of outstanding invoices
 * @property {number} data.paid_invoices - Number of paid invoices
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useBillingStats = (params = {}) => {
  return useQuery({
    queryKey: ['billing', 'stats', params],
    queryFn: async () => {
      const endpoint = API_ENDPOINTS.BILLING_STATS || '/api/v1/billing/stats/summary';
      const response = await api.get(endpoint, { params });
      return response.data;
    },
  });
};

/**
 * Query hook to fetch billing revenue data
 * 
 * @param {Object} params - Query parameters
 * @returns {Object} React Query result with revenue data
 */
export const useBillingRevenue = (params = {}) => {
  return useQuery({
    queryKey: ['billing', 'revenue', params],
    queryFn: async () => {
      const endpoint = `${API_ENDPOINTS.BILLING || '/api/v1/billing'}/revenue`;
      const response = await api.get(endpoint, { params });
      const data = response.data || {};
      return Array.isArray(data.revenue) ? data.revenue : (Array.isArray(data) ? data : []);
    },
  });
};

/**
 * Query hook to fetch client segmentation data
 * 
 * @param {Object} params - Query parameters
 * @returns {Object} React Query result with client segmentation data
 */
export const useClientSegmentation = (params = {}) => {
  return useQuery({
    queryKey: ['billing', 'client-segmentation', params],
    queryFn: async () => {
      const endpoint = `${API_ENDPOINTS.BILLING || '/api/v1/billing'}/client-segmentation`;
      const response = await api.get(endpoint, { params });
      const data = response.data || {};
      return Array.isArray(data.segmentation) ? data.segmentation : (Array.isArray(data) ? data : []);
    },
  });
};

/**
 * Query hook to fetch billing AI insights
 * 
 * @param {Object} params - Query parameters
 * @returns {Object} React Query result with AI insights data
 */
export const useBillingAIInsights = (params = {}) => {
  return useQuery({
    queryKey: ['billing', 'ai-insights', params],
    queryFn: async () => {
      const endpoint = `${API_ENDPOINTS.BILLING || '/api/v1/billing'}/ai-insights`;
      const response = await api.get(endpoint, { params });
      const data = response.data || {};
      return Array.isArray(data.insights) ? data.insights : (Array.isArray(data) ? data : []);
    },
  });
};

/**
 * Hook to get billing balance
 * @returns {Object} Query result with billing balance data
 */
export const useBillingBalance = () => {
  return useQuery({
    queryKey: ['billing', 'balance'],
    queryFn: async () => {
      const { data } = await api.get('/api/v1/billing/billing/balance');
      return data;
    },
  });
};

/**
 * Get invoice line items
 * @param {string} invoiceId - Invoice ID
 * @returns {Object} React Query result
 */
export const useInvoiceLineItems = (invoiceId) => {
  return useQuery({
    queryKey: ['invoices', invoiceId, 'line-items'],
    queryFn: async () => {
      const response = await api.get(`/api/v1/billing/invoices/${invoiceId}/line-items`);
      return response.data;
    },
    enabled: !!invoiceId,
  });
};

/**
 * Generate invoice preview mutation
 * @returns {Object} React Query mutation result
 */
export const useGenerateInvoicePreview = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ clientId, dateFrom, dateTo }) => {
      const response = await api.post('/api/v1/billing/invoices/generate-preview', {
        client_id: clientId,
        date_from: dateFrom,
        date_to: dateTo,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
};

/**
 * Download invoice PDF mutation
 * @returns {Object} React Query mutation result
 */
export const useDownloadInvoicePDF = () => {
  return useMutation({
    mutationFn: async (invoiceId) => {
      const response = await api.get(`/api/v1/billing/invoices/${invoiceId}/pdf`, {
        responseType: 'blob',
      });
      return response.data;
    },
  });
};

/**
 * Send invoice email mutation
 * @returns {Object} React Query mutation result
 */
export const useSendInvoiceEmail = () => {
  return useMutation({
    mutationFn: async ({ invoiceId, recipients }) => {
      const response = await api.post(`/api/v1/billing/invoices/${invoiceId}/send`, {
        recipients,
      });
      return response.data;
    },
  });
};

/**
 * Get billing estimate
 * @param {string} clientId - Client ID
 * @param {string} dateFrom - Start date
 * @param {string} dateTo - End date
 * @returns {Object} React Query result
 */
export const useBillingEstimate = (clientId, dateFrom, dateTo) => {
  return useQuery({
    queryKey: ['billing', 'estimate', clientId, dateFrom, dateTo],
    queryFn: async () => {
      const response = await api.get(`/api/v1/billing/estimate/${clientId}`, {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      return response.data;
    },
    enabled: !!clientId && !!dateFrom && !!dateTo,
  });
};

/**
 * Get operation costs
 * @param {string} clientId - Client ID
 * @returns {Object} React Query result
 */
export const useOperationCosts = (clientId) => {
  return useQuery({
    queryKey: ['billing', 'operation-costs', clientId],
    queryFn: async () => {
      const response = await api.get('/api/v1/billing/operation-costs', {
        params: { client_id: clientId },
      });
      return response.data;
    },
    enabled: !!clientId,
  });
};

/**
 * Create operation cost mutation
 * @returns {Object} React Query mutation result
 */
export const useCreateOperationCost = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/billing/operation-costs', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'operation-costs'] });
    },
  });
};

/**
 * Update operation cost mutation
 * @returns {Object} React Query mutation result
 */
export const useUpdateOperationCost = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ costId, data }) => {
      const response = await api.put(`/api/v1/billing/operation-costs/${costId}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'operation-costs'] });
    },
  });
};

/**
 * Delete operation cost mutation
 * @returns {Object} React Query mutation result
 */
export const useDeleteOperationCost = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (costId) => {
      const response = await api.delete(`/api/v1/billing/operation-costs/${costId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'operation-costs'] });
    },
  });
};

/**
 * Get client rate cards
 * @param {string} clientId - Client ID
 * @returns {Object} React Query result
 */
export const useClientRateCards = (clientId) => {
  return useQuery({
    queryKey: ['billing', 'client-rate-cards', clientId],
    queryFn: async () => {
      const response = await api.get('/api/v1/billing/client-rate-cards', {
        params: { client_id: clientId },
      });
      return response.data;
    },
    enabled: !!clientId,
  });
};

/**
 * Create client rate card mutation
 * @returns {Object} React Query mutation result
 */
export const useCreateClientRateCard = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/billing/client-rate-cards', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'client-rate-cards'] });
    },
  });
};

/**
 * Update client rate card mutation
 * @returns {Object} React Query mutation result
 */
export const useUpdateClientRateCard = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ rateCardId, data }) => {
      const response = await api.put(`/api/v1/billing/client-rate-cards/${rateCardId}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'client-rate-cards'] });
    },
  });
};

/**
 * Delete client rate card mutation
 * @returns {Object} React Query mutation result
 */
export const useDeleteClientRateCard = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (rateCardId) => {
      const response = await api.delete(`/api/v1/billing/client-rate-cards/${rateCardId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'client-rate-cards'] });
    },
  });
};

/**
 * Get surcharge rules
 * @param {Object} params - Query parameters
 * @returns {Object} React Query result
 */
export const useSurchargeRules = (params = {}) => {
  return useQuery({
    queryKey: ['billing', 'surcharge-rules', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/billing/surcharge-rules', { params });
      return response.data;
    },
  });
};

/**
 * Create surcharge rule mutation
 * @returns {Object} React Query mutation result
 */
export const useCreateSurchargeRule = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/billing/surcharge-rules', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'surcharge-rules'] });
    },
  });
};

/**
 * Update surcharge rule mutation
 * @returns {Object} React Query mutation result
 */
export const useUpdateSurchargeRule = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ ruleId, data }) => {
      const response = await api.put(`/api/v1/billing/surcharge-rules/${ruleId}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'surcharge-rules'] });
    },
  });
};

/**
 * Delete surcharge rule mutation
 * @returns {Object} React Query mutation result
 */
export const useDeleteSurchargeRule = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (ruleId) => {
      const response = await api.delete(`/api/v1/billing/surcharge-rules/${ruleId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'surcharge-rules'] });
    },
  });
};

/**
 * Test surcharge rule mutation
 * @returns {Object} React Query mutation result
 */
export const useTestSurchargeRule = () => {
  return useMutation({
    mutationFn: async ({ ruleId, testData }) => {
      const response = await api.post(`/api/v1/billing/surcharge-rules/${ruleId}/test`, testData);
      return response.data;
    },
  });
};

/**
 * Get account balance
 * @param {string} clientId - Client ID
 * @returns {Object} React Query result
 */
export const useAccountBalance = (clientId) => {
  return useQuery({
    queryKey: ['billing', 'account-balance', clientId],
    queryFn: async () => {
      const response = await api.get(`/api/v1/billing/account-balance/${clientId}`);
      return response.data;
    },
    enabled: !!clientId,
  });
};

/**
 * Top up account mutation
 * @returns {Object} React Query mutation result
 */
export const useTopUpAccount = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ clientId, amount, paymentMethod }) => {
      const response = await api.post(`/api/v1/billing/account-balance/${clientId}/topup`, {
        amount,
        payment_method: paymentMethod,
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'account-balance', variables.clientId] });
    },
  });
};

/**
 * Get balance history
 * @param {string} clientId - Client ID
 * @param {string} dateFrom - Start date
 * @param {string} dateTo - End date
 * @returns {Object} React Query result
 */
export const useBalanceHistory = (clientId, dateFrom, dateTo) => {
  return useQuery({
    queryKey: ['billing', 'balance-history', clientId, dateFrom, dateTo],
    queryFn: async () => {
      const response = await api.get(`/api/v1/billing/account-balance/${clientId}/history`, {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      return response.data;
    },
    enabled: !!clientId && !!dateFrom && !!dateTo,
  });
};

/**
 * Get billable events
 * @param {Object} params - Query parameters
 * @returns {Object} React Query result
 */
export const useBillableEvents = (params = {}) => {
  return useQuery({
    queryKey: ['billing', 'billable-events', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/billing/billable-events', { params });
      return response.data;
    },
  });
};

/**
 * Hook to get API keys
 * @returns {Object} Query result with API keys data
 */
export const useApiKeys = () => {
  return useQuery({
    queryKey: ['billing', 'api-keys'],
    queryFn: async () => {
      const response = await api.get('/api/v1/billing/partner-api/api-keys');
      const data = response.data;
      return Array.isArray(data) ? data : (data?.items || data?.keys || []);
    },
  });
};

/**
 * Hook to create a new API key
 * @returns {Object} Mutation object for creating API keys
 */
export const useCreateApiKey = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (keyData) => {
      const response = await api.post('/api/v1/billing/partner-api/api-keys', keyData);
      return response.data?.apiKey || response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'api-keys'] });
    },
  });
};

/**
 * Hook to delete an API key
 * @returns {Object} Mutation object for deleting API keys
 */
export const useDeleteApiKey = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (keyId) => {
      const response = await api.delete(`/api/v1/billing/partner-api/api-keys/${keyId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'api-keys'] });
    },
  });
};

/**
 * Hook to create a subscription
 * @returns {Object} Mutation object for creating subscriptions
 */
export const useCreateSubscription = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (subscriptionData) => {
      const response = await api.post('/api/v1/billing/subscriptions', subscriptionData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });
};

/**
 * Fetches billing models with optional filtering
 * 
 * @param {Object} filters - Query parameters for filtering billing models
 * @param {string} [filters.warehouse_type] - Filter by warehouse type
 * @param {string} [filters.search] - Search term
 * @param {number} [filters.page] - Page number for pagination
 * @param {number} [filters.limit] - Items per page
 * @returns {Object} React Query result with billing models data
 * @property {Object} data - Billing models response object
 * @property {Array} data.models - Array of billing model records
 * @property {number} data.total - Total number of billing models
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useBillingModels = (filters = {}) => {
  
  return useQuery({
    queryKey: ['billing', 'models', filters],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: (filters.page || 0).toString(),
        limit: (filters.limit || 10).toString(),
        ...(filters.warehouse_type && { warehouse_type: filters.warehouse_type }),
        ...(filters.search && { search: filters.search })
      });
      const response = await api.get(`${API_ENDPOINTS.BILLING}/billing-models?${params}`);
      return response.data;
    },
  });
};

/**
 * Mutation hook to delete a billing model
 * 
 * Automatically invalidates billing models query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const deleteModel = useDeleteBillingModel();
 * deleteModel.mutate('model_123');
 */
export const useDeleteBillingModel = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (modelId) => {
      const response = await api.delete(`${API_ENDPOINTS.BILLING}/billing-models/${modelId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'models'] });
    },
  });
};

/**
 * Fetches a single billing model by ID
 * 
 * Query is disabled if modelId is not provided
 * 
 * @param {string} modelId - Billing model ID
 * @returns {Object} React Query result with billing model data
 * @property {Object} data - Billing model record with full details
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useBillingModel = (modelId) => {
  
  return useQuery({
    queryKey: ['billing', 'models', modelId],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.BILLING}/billing-models/${modelId}`);
      return response.data;
    },
    enabled: !!modelId,
  });
};

/**
 * Mutation hook to create a new billing model
 * 
 * Automatically invalidates billing models query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 */
export const useCreateBillingModel = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (modelData) => {
      const response = await api.post(`${API_ENDPOINTS.BILLING}/billing-models`, modelData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'models'] });
    },
  });
};

/**
 * Mutation hook to update an existing billing model
 * 
 * Automatically invalidates both the specific model and models list on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 */
export const useUpdateBillingModel = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ modelId, data }) => {
      const response = await api.put(`${API_ENDPOINTS.BILLING}/billing-models/${modelId}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'models', variables.modelId] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'models'] });
    },
  });
};

/**
 * Fetches billing assignments
 * 
 * @returns {Object} React Query result with billing assignments data
 * @property {Array} data - Array of assignment records
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useBillingAssignments = () => {
  
  return useQuery({
    queryKey: ['billing', 'assignments'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.BILLING}/assignments`);
      const data = response.data || {};
      return Array.isArray(data.assignments) ? data.assignments : (Array.isArray(data) ? data : []);
    },
  });
};

/**
 * Mutation hook to create a billing assignment
 * 
 * Automatically invalidates assignments query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 */
export const useCreateBillingAssignment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (assignmentData) => {
      const response = await api.post(`${API_ENDPOINTS.BILLING}/assignments`, assignmentData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'assignments'] });
    },
  });
};

/**
 * Mutation hook to calculate billing for a warehouse
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {Function} mutateAsync - Async version of mutate
 * @property {boolean} isPending - Mutation pending state
 */
export const useCalculateBilling = () => {
  return useMutation({
    mutationFn: async ({ warehouse_id, start_date, end_date }) => {
      const { data } = await api.post(`${API_ENDPOINTS.BILLING}/calculate`, {
        warehouse_id,
        start_date,
        end_date
      });
      return data;
    },
  });
};

/**
 * Mutation hook to generate billing report
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {Function} mutateAsync - Async version of mutate
 * @property {boolean} isPending - Mutation pending state
 */
export const useGenerateBillingReport = () => {
  return useMutation({
    mutationFn: async ({ warehouse_ids, start_date, end_date }) => {
      const response = await api.post(`${API_ENDPOINTS.BILLING}/reports/generate`, {
        warehouse_ids,
        start_date,
        end_date
      });
      return response.data;
    },
  });
};

/**
 * Pricing Groups Hooks
 * Customer pricing groups for tiered pricing management
 */

/**
 * Fetch all pricing groups
 * @param {Object} params - Query parameters
 * @returns {Object} React Query result with pricing groups data
 */
export const usePricingGroups = (params = {}) => {
  return useQuery({
    queryKey: ['pricing-groups', params],
    queryFn: async () => {
      try {
        const response = await api.get('/api/v1/pricing-groups/', { params });
        const data = response.data;
        // Ensure we return an array or object with items array
        if (Array.isArray(data)) {
          return { items: data };
        }
        if (data && Array.isArray(data.items)) {
          return data;
        }
        // Return empty items array if data format is unexpected
        return { items: [] };
      } catch (error) {
        console.warn('Pricing groups endpoint not available:', error?.response?.status);
        return { items: [] };
      }
    },
    retry: false,
  });
};

/**
 * Fetch a single pricing group by ID
 * @param {string} groupId - Pricing group ID
 * @returns {Object} React Query result with pricing group data
 */
export const usePricingGroup = (groupId) => {
  return useQuery({
    queryKey: ['pricing-groups', groupId],
    queryFn: async () => {
      const response = await api.get(`/api/v1/pricing-groups/${groupId}`);
      return response.data;
    },
    enabled: !!groupId,
  });
};

/**
 * Create a new pricing group
 * @returns {Object} React Query mutation result
 */
export const useCreatePricingGroup = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/pricing-groups/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-groups'] });
    },
  });
};

/**
 * Update a pricing group
 * @returns {Object} React Query mutation result
 */
export const useUpdatePricingGroup = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ groupId, data }) => {
      const response = await api.put(`/api/v1/pricing-groups/${groupId}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pricing-groups', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['pricing-groups'] });
    },
  });
};

/**
 * Delete a pricing group
 * @returns {Object} React Query mutation result
 */
export const useDeletePricingGroup = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (groupId) => {
      const response = await api.delete(`/api/v1/pricing-groups/${groupId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-groups'] });
    },
  });
};

/**
 * Fetch the default pricing group
 * @returns {Object} React Query result with default pricing group data
 */
export const useDefaultPricingGroup = () => {
  return useQuery({
    queryKey: ['pricing-groups', 'default'],
    queryFn: async () => {
      const response = await api.get('/api/v1/pricing-groups/default');
      return response.data;
    },
  });
};

/**
 * Fetch customer's pricing group
 * @param {string} customerId - Customer ID
 * @returns {Object} React Query result with customer's pricing group data
 */
export const useCustomerPricingGroup = (customerId) => {
  return useQuery({
    queryKey: ['pricing-groups', 'customer', customerId],
    queryFn: async () => {
      const response = await api.get(`/api/v1/pricing-groups/customer/${customerId}`);
      return response.data;
    },
    enabled: !!customerId,
  });
};

/**
 * Fetch pricing matrix entries with optional filters
 * @param {Object} params - Query parameters (pricing_group_id, carrier_id, country_id)
 * @returns {Object} React Query result with pricing matrix data
 */
export const usePricingMatrix = (params = {}) => {
  return useQuery({
    queryKey: ['pricing-groups', 'matrix', params],
    queryFn: async () => {
      try {
        const response = await api.get('/api/v1/pricing-groups/matrix/', { params });
        const data = response.data;
        if (Array.isArray(data)) {
          return { items: data };
        }
        if (data && Array.isArray(data.items)) {
          return data;
        }
        return { items: [] };
      } catch (error) {
        console.warn('Pricing matrix endpoint not available:', error?.response?.status);
        return { items: [] };
      }
    },
    retry: false,
  });
};

/**
 * Add price to pricing matrix
 * @returns {Object} React Query mutation result
 */
export const useAddPricingMatrixPrice = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ groupId, data }) => {
      const response = await api.post(`/api/v1/pricing-groups/${groupId}/matrix`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-groups', 'matrix'] });
    },
  });
};

/**
 * Bulk import prices to pricing matrix
 * @returns {Object} React Query mutation result
 */
export const useBulkImportPrices = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ groupId, prices }) => {
      const response = await api.post(`/api/v1/pricing-groups/${groupId}/matrix/bulk`, { prices });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-groups', 'matrix'] });
    },
  });
};

/**
 * Fetch pricing surcharges with optional filters
 * @param {Object} params - Query parameters (pricing_group_id, carrier_id, country_id)
 * @returns {Object} React Query result with surcharges data
 */
export const usePricingSurcharges = (params = {}) => {
  return useQuery({
    queryKey: ['pricing-groups', 'surcharges', params],
    queryFn: async () => {
      try {
        const response = await api.get('/api/v1/pricing-groups/surcharges/', { params });
        const data = response.data;
        if (Array.isArray(data)) {
          return { items: data };
        }
        if (data && Array.isArray(data.items)) {
          return data;
        }
        return { items: [] };
      } catch (error) {
        console.warn('Pricing surcharges endpoint not available:', error?.response?.status);
        return { items: [] };
      }
    },
    retry: false,
  });
};

/**
 * Add surcharge to pricing group
 * @returns {Object} React Query mutation result
 */
export const useAddPricingSurcharge = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ groupId, data }) => {
      const response = await api.post(`/api/v1/pricing-groups/${groupId}/surcharges`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pricing-groups', variables.groupId, 'surcharges'] });
    },
  });
};

/**
 * Calculate quote for a customer
 * @returns {Object} React Query mutation result
 */
export const useCalculatePricingQuote = () => {
  return useMutation({
    mutationFn: async ({ customerId, carrierId, countryCode, weight, postcode }) => {
      const response = await api.post('/api/v1/pricing-groups/quote', {
        customer_id: customerId,
        carrier_id: carrierId,
        country_code: countryCode,
        weight,
        postcode,
      });
      return response.data;
    },
  });
};

/**
 * Assign customer to pricing group
 * @returns {Object} React Query mutation result
 */
export const useAssignCustomerToPricingGroup = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ customerId, groupId }) => {
      const response = await api.post('/api/v1/pricing-groups/customer/assign', {
        customer_id: customerId,
        pricing_group_id: groupId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-groups'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
};

/**
 * Invoice Items Hooks
 * Manage invoice line items for detailed billing
 */

/**
 * Fetch invoice items for a specific invoice
 * @param {number} invoiceId - Invoice ID
 * @param {Object} params - Query parameters
 * @returns {Object} React Query result with invoice items data
 */
export const useInvoiceItems = (invoiceId, params = {}) => {
  return useQuery({
    queryKey: ['invoice-items', invoiceId, params],
    queryFn: async () => {
      const response = await api.get('/api/v1/invoice-items/', { 
        params: { invoice_id: invoiceId, ...params } 
      });
      return response.data;
    },
    enabled: !!invoiceId,
  });
};

/**
 * Fetch a single invoice item by ID
 * @param {number} itemId - Invoice item ID
 * @returns {Object} React Query result with invoice item data
 */
export const useInvoiceItem = (itemId) => {
  return useQuery({
    queryKey: ['invoice-items', itemId],
    queryFn: async () => {
      const response = await api.get(`/api/v1/invoice-items/${itemId}`);
      return response.data;
    },
    enabled: !!itemId,
  });
};

/**
 * Create a new invoice item
 * @returns {Object} React Query mutation result
 */
export const useCreateInvoiceItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/invoice-items/', data);
      return response.data;
    },
    onSuccess: (_data) => {
      queryClient.invalidateQueries({ queryKey: ['invoice-items'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
};

/**
 * Update an invoice item
 * @returns {Object} React Query mutation result
 */
export const useUpdateInvoiceItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ itemId, data }) => {
      const response = await api.patch(`/api/v1/invoice-items/${itemId}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoice-items', variables.itemId] });
      queryClient.invalidateQueries({ queryKey: ['invoice-items'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
};

/**
 * Delete an invoice item
 * @returns {Object} React Query mutation result
 */
export const useDeleteInvoiceItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (itemId) => {
      const response = await api.delete(`/api/v1/invoice-items/${itemId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice-items'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
};

/**
 * Bulk create invoice items
 * @returns {Object} React Query mutation result
 */
export const useBulkCreateInvoiceItems = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ invoiceId, items }) => {
      const response = await api.post('/api/v1/invoice-items/bulk', { 
        invoice_id: invoiceId, 
        items 
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice-items'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
};

/**
 * Fetch invoice item categories
 * @returns {Object} React Query result with categories data
 */
export const useInvoiceItemCategories = () => {
  return useQuery({
    queryKey: ['invoice-items', 'categories'],
    queryFn: async () => {
      const response = await api.get('/api/v1/invoice-items/categories');
      return response.data;
    },
  });
};

/**
 * Fetch invoice items by category
 * @param {number} invoiceId - Invoice ID
 * @param {string} category - Category name
 * @returns {Object} React Query result with filtered items
 */
export const useInvoiceItemsByCategory = (invoiceId, category) => {
  return useQuery({
    queryKey: ['invoice-items', invoiceId, 'category', category],
    queryFn: async () => {
      const response = await api.get(`/api/v1/invoice-items/by-category/${invoiceId}/${category}`);
      return response.data;
    },
    enabled: !!invoiceId && !!category,
  });
};

/**
 * Invoice Templates Hooks
 * Manage reusable invoice templates
 */

/**
 * Fetch all invoice templates
 * @param {Object} params - Query parameters
 * @returns {Object} React Query result with templates data
 */
export const useInvoiceTemplates = (params = {}) => {
  return useQuery({
    queryKey: ['invoice-templates', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/invoice-templates/', { params });
      return response.data;
    },
  });
};

/**
 * Fetch a single invoice template by ID
 * @param {number} templateId - Template ID
 * @returns {Object} React Query result with template data
 */
export const useInvoiceTemplate = (templateId) => {
  return useQuery({
    queryKey: ['invoice-templates', templateId],
    queryFn: async () => {
      const response = await api.get(`/api/v1/invoice-templates/${templateId}`);
      return response.data;
    },
    enabled: !!templateId,
  });
};

/**
 * Fetch invoice template by type
 * @param {string} templateType - Template type (picking, receiving, etc.)
 * @returns {Object} React Query result with template data
 */
export const useInvoiceTemplateByType = (templateType) => {
  return useQuery({
    queryKey: ['invoice-templates', 'type', templateType],
    queryFn: async () => {
      const response = await api.get(`/api/v1/invoice-templates/by-type/${templateType}`);
      return response.data;
    },
    enabled: !!templateType,
  });
};

/**
 * Fetch available template types
 * @returns {Object} React Query result with template types
 */
export const useInvoiceTemplateTypes = () => {
  return useQuery({
    queryKey: ['invoice-templates', 'types'],
    queryFn: async () => {
      const response = await api.get('/api/v1/invoice-templates/types');
      return response.data;
    },
  });
};

/**
 * Create a new invoice template
 * @returns {Object} React Query mutation result
 */
export const useCreateInvoiceTemplate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/invoice-templates/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice-templates'] });
    },
  });
};

/**
 * Update an invoice template
 * @returns {Object} React Query mutation result
 */
export const useUpdateInvoiceTemplate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ templateId, data }) => {
      const response = await api.patch(`/api/v1/invoice-templates/${templateId}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoice-templates', variables.templateId] });
      queryClient.invalidateQueries({ queryKey: ['invoice-templates'] });
    },
  });
};

/**
 * Delete an invoice template
 * @returns {Object} React Query mutation result
 */
export const useDeleteInvoiceTemplate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (templateId) => {
      const response = await api.delete(`/api/v1/invoice-templates/${templateId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice-templates'] });
    },
  });
};

/**
 * Duplicate an invoice template
 * @returns {Object} React Query mutation result
 */
export const useDuplicateInvoiceTemplate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ templateId, newName }) => {
      const response = await api.post(`/api/v1/invoice-templates/${templateId}/duplicate`, { 
        new_name: newName 
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice-templates'] });
    },
  });
};

/**
 * Apply template to an invoice
 * @returns {Object} React Query mutation result
 */
export const useApplyInvoiceTemplate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ templateId, invoiceId }) => {
      const response = await api.post(`/api/v1/invoice-templates/${templateId}/apply`, { 
        invoice_id: invoiceId 
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-items'] });
    },
  });
};

/**
 * Invoice History Hooks
 * Track invoice audit trail and history
 */

/**
 * Fetch invoice history for a specific invoice
 * @param {number} invoiceId - Invoice ID
 * @returns {Object} React Query result with history data
 */
export const useInvoiceHistory = (invoiceId) => {
  return useQuery({
    queryKey: ['invoice-history', 'invoice', invoiceId],
    queryFn: async () => {
      const response = await api.get(`/api/v1/invoice-history/invoice/${invoiceId}`);
      return response.data;
    },
    enabled: !!invoiceId,
  });
};

/**
 * Fetch a single history entry by ID
 * @param {number} historyId - History entry ID
 * @returns {Object} React Query result with history entry data
 */
export const useInvoiceHistoryEntry = (historyId) => {
  return useQuery({
    queryKey: ['invoice-history', historyId],
    queryFn: async () => {
      const response = await api.get(`/api/v1/invoice-history/${historyId}`);
      return response.data;
    },
    enabled: !!historyId,
  });
};

/**
 * Fetch invoice status transitions
 * @param {number} invoiceId - Invoice ID
 * @returns {Object} React Query result with status transitions
 */
export const useInvoiceStatusTransitions = (invoiceId) => {
  return useQuery({
    queryKey: ['invoice-history', 'invoice', invoiceId, 'transitions'],
    queryFn: async () => {
      const response = await api.get(`/api/v1/invoice-history/invoice/${invoiceId}/transitions`);
      return response.data;
    },
    enabled: !!invoiceId,
  });
};

/**
 * Fetch invoice activity summary
 * @param {number} invoiceId - Invoice ID
 * @returns {Object} React Query result with activity summary
 */
export const useInvoiceActivitySummary = (invoiceId) => {
  return useQuery({
    queryKey: ['invoice-history', 'invoice', invoiceId, 'summary'],
    queryFn: async () => {
      const response = await api.get(`/api/v1/invoice-history/invoice/${invoiceId}/summary`);
      return response.data;
    },
    enabled: !!invoiceId,
  });
};

/**
 * Fetch user activity on invoices
 * @param {number} userId - User ID
 * @returns {Object} React Query result with user activity
 */
export const useUserInvoiceActivity = (userId) => {
  return useQuery({
    queryKey: ['invoice-history', 'user', userId],
    queryFn: async () => {
      const response = await api.get(`/api/v1/invoice-history/user/${userId}`);
      return response.data;
    },
    enabled: !!userId,
  });
};

/**
 * Fetch recent invoice activity
 * @param {Object} params - Query parameters (limit, partner_id)
 * @returns {Object} React Query result with recent activity
 */
export const useRecentInvoiceActivity = (params = {}) => {
  return useQuery({
    queryKey: ['invoice-history', 'recent', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/invoice-history/recent', { params });
      return response.data;
    },
  });
};

/**
 * Search invoice history
 * @param {Object} params - Search parameters
 * @returns {Object} React Query result with search results
 */
export const useSearchInvoiceHistory = (params = {}) => {
  return useQuery({
    queryKey: ['invoice-history', 'search', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/invoice-history/search', { params });
      return response.data;
    },
    enabled: Object.keys(params).length > 0,
  });
};

/**
 * Fetch available history actions
 * @returns {Object} React Query result with action types
 */
export const useInvoiceHistoryActions = () => {
  return useQuery({
    queryKey: ['invoice-history', 'actions'],
    queryFn: async () => {
      const response = await api.get('/api/v1/invoice-history/actions');
      return response.data;
    },
  });
};

/**
 * Record a history entry
 * @returns {Object} React Query mutation result
 */
export const useRecordInvoiceHistory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/invoice-history/', data);
      return response.data;
    },
    onSuccess: (_data) => {
      queryClient.invalidateQueries({ queryKey: ['invoice-history'] });
    },
  });
};

/**
 * Invoice Workflow Hooks
 * Manage invoice lifecycle operations
 */

/**
 * Validate an invoice (draft -> pending)
 * @returns {Object} React Query mutation result
 */
export const useValidateInvoice = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ invoiceId, changeReason }) => {
      const response = await api.post(`/api/v1/invoices/${invoiceId}/validate`, { 
        change_reason: changeReason 
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-history'] });
    },
  });
};

/**
 * Send an invoice (pending -> sent)
 * @returns {Object} React Query mutation result
 */
export const useSendInvoice = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ invoiceId, email, changeReason }) => {
      const response = await api.post(`/api/v1/invoices/${invoiceId}/send`, { 
        email,
        change_reason: changeReason 
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-history'] });
    },
  });
};

/**
 * Create a credit note for an invoice
 * @returns {Object} React Query mutation result
 */
export const useCreateCreditNote = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ invoiceId, amount, reason }) => {
      const response = await api.post(`/api/v1/invoices/${invoiceId}/credit-note`, { 
        amount,
        reason 
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-history'] });
    },
  });
};

/**
 * Apply late fee to an invoice
 * @returns {Object} React Query mutation result
 */
export const useApplyLateFee = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ invoiceId, feePercentage }) => {
      const response = await api.post(`/api/v1/invoices/${invoiceId}/late-fee`, { 
        fee_percentage: feePercentage 
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-history'] });
    },
  });
};

/**
 * Apply discount to an invoice
 * @returns {Object} React Query mutation result
 */
export const useApplyInvoiceDiscount = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ invoiceId, discountCode, discountAmount }) => {
      const response = await api.post(`/api/v1/invoices/${invoiceId}/discount`, { 
        discount_code: discountCode,
        discount_amount: discountAmount 
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-history'] });
    },
  });
};

/**
 * Send payment reminder for an invoice
 * @returns {Object} React Query mutation result
 */
export const useSendPaymentReminder = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ invoiceId, message }) => {
      const response = await api.post(`/api/v1/invoices/${invoiceId}/reminder`, { 
        message 
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoice-history'] });
    },
  });
};

/**
 * Fetch overdue invoices
 * @param {number} daysOverdue - Minimum days overdue (default: 0)
 * @returns {Object} React Query result with overdue invoices
 */
export const useOverdueInvoices = (daysOverdue = 0) => {
  return useQuery({
    queryKey: ['invoices', 'overdue', daysOverdue],
    queryFn: async () => {
      const response = await api.get('/api/v1/invoices/overdue', { 
        params: { days_overdue: daysOverdue } 
      });
      return response.data;
    },
  });
};

// =====================================================
// Billing Management Hooks (merged from useBillingManagement)
// =====================================================

export const useGetBillingAccounts = (params = {}) => {
  return useQuery({
    queryKey: ['billing', 'accounts', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/billing/accounts', { params });
      return response.data;
    },
  });
};

export const useGetBillingAccount = (params = {}) => {
  return useQuery({
    queryKey: ['billing', 'account', params],
    queryFn: async () => {
      const response = await api.get(`/api/v1/billing/accounts/${params.id}`);
      return response.data;
    },
    enabled: !!params.id,
  });
};

export const useCreateBillingAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/billing/accounts', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'accounts'] });
    },
  });
};

export const useUpdateBillingAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.put(`/api/v1/billing/accounts/${data.id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'accounts'] });
    },
  });
};

export const useGetInvoices = (params = {}) => {
  return useQuery({
    queryKey: ['billing', 'invoicesList', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/billing/invoices', { params });
      return response.data;
    },
  });
};

export const useGetInvoice = (params = {}) => {
  return useQuery({
    queryKey: ['billing', 'invoiceById', params],
    queryFn: async () => {
      const response = await api.get(`/api/v1/billing/invoices/${params.id}`);
      return response.data;
    },
    enabled: !!params.id,
  });
};

export const useUpdateInvoiceStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.put(`/api/v1/billing/invoices/${data.id}/status`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
};

export const useGetPayments = (params = {}) => {
  return useQuery({
    queryKey: ['billing', 'paymentsList', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/billing/payments', { params });
      return response.data;
    },
  });
};

export const useCreatePayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/billing/payments', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'paymentsList'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
  });
};

export const useGetBillingRates = (params = {}) => {
  return useQuery({
    queryKey: ['billing', 'rates', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/billing/rates', { params });
      return response.data;
    },
  });
};

export const useCreateBillingRate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/billing/rates', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'rates'] });
    },
  });
};

export const useGetBillingCycles = (params = {}) => {
  return useQuery({
    queryKey: ['billing', 'cycles', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/billing/cycles', { params });
      return response.data;
    },
  });
};

export const useCreateBillingAdjustment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/billing/adjustments', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });
};

export const useGetBillingStats = (params = {}) => {
  return useQuery({
    queryKey: ['billing', 'statsList', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/billing/stats', { params });
      return response.data;
    },
  });
};
